import { useState } from 'react'
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Mountain,
  Music,
  Plus,
  Signpost,
  Tent,
  Trash2,
} from 'lucide-react'

import { Field } from '../../components/Field'
import { TulipSketch } from '../../components/TulipSketch'
import { useRoadbook } from '../../data/roadbookContext'
import { travelDayLabel } from '../../lib/calendar'
import { hours, num } from '../../lib/format'
import { coordText } from '../../lib/geocode'
import { routeState } from '../../lib/route'
import { startHerkunft } from '../../lib/trip'
import { tulipKm, tulipText } from '../../lib/tulip'
import { STAGE_TYPES, type Stage } from '../../lib/types'
import { AddressSearch } from './AddressSearch'
import { ErsteEtappe } from './ErsteEtappe'
import { FahrzeitenPanel } from './FahrzeitenPanel'
import { StageContentEditor } from './StageContentEditor'
import { StageRouteEditor } from './StageRouteEditor'
import { StageSurfacePanel } from './StageSurfacePanel'
import { StageRoutesPanel } from './StageRoutesPanel'

interface Props {
  selectedId: string
  onSelect: (id: string) => void
}

export function StagesView({ selectedId, onSelect }: Props) {
  const { trip, darfSchreiben, addStage } = useRoadbook()
  const leer = trip.stages.length === 0
  /**
   * Ob die erste Etappe angefragt und nicht angelegt wurde.
   *
   * Das gibt es in genau einem Fall: der Abgleich hat Etappen von einem anderen
   * Gerät gebracht, während die Maske noch offen war. Die Reise ist damit nicht
   * mehr leer, ihr Beginn steht schon fest, und insertStageAfter legt nichts
   * mehr an. Die Maske verschwindet im selben Zug - der Hinweis gehört deshalb
   * hierher und nicht in sie hinein, sonst stünde er in einem Teil der
   * Oberfläche, den es im nächsten Bild nicht mehr gibt.
   */
  const [startVerworfen, setStartVerworfen] = useState(false)
  const [etappeDanachVerworfen, setEtappeDanachVerworfen] = useState(false)

  return (
    <section className="seite">
      {startVerworfen && !leer && (
        <p className="karte-warnung kein-druck">
          Die Reise hat inzwischen Etappen — vermutlich von einem anderen Gerät. Ihr Beginn steht
          damit schon fest, und dein Startpunkt wurde nicht angelegt. Stimmt er nicht, gehört er in
          die Startfelder der ersten Etappe.
        </p>
      )}
      {etappeDanachVerworfen && (
        <p className="karte-warnung kein-druck">
          Die Etappe ist inzwischen verschwunden — vermutlich wurde sie auf einem anderen Gerät
          gelöscht. Deshalb wurde keine neue Etappe dahinter angelegt.
        </p>
      )}

      {/* Der Sammelaufruf schreibt Strecken in die Etappen: ohne Schreibrecht
          gibt es hier nichts zu holen, und ohne Etappen erst recht nicht -
          sonst stünde dort "Alle Strecken berechnet" über einer Reise, die
          keine hat. */}
      {darfSchreiben && !leer && <StageRoutesPanel />}

      {/* Anders als der Sammelaufruf darüber schreibt dieser Block nichts: er
          stellt den Aufzeichnungen die geschätzten Fahrzeiten gegenüber. Also
          steht er auch dem offen, der nur zusehen darf - und auf einer Reise
          ohne Aufzeichnung zeigt er von sich aus nichts. */}
      {!leer && <FahrzeitenPanel />}

      {trip.stages.map((stage) => (
        <StageCard
          key={stage.id}
          stage={stage}
          // Anders als überall sonst steht in dieser Liste auch, was
          // ausgelassen ist - hier wird ja darüber entschieden. Eine
          // ausgelassene Etappe bekommt deshalb keinen Tag, sondern einen
          // Strich: sie kostet keinen, und das soll man an der Liste sehen.
          tag={travelDayLabel(trip.stages, stage)}
          open={selectedId === stage.id}
          onToggle={() => {
            // Mit der nächsten Etappenhandlung ist der verworfene Versuch
            // erledigt. Der Hinweis soll den Konflikt erklären, nicht durch
            // die weitere Arbeit an der Reise kleben bleiben.
            setStartVerworfen(false)
            setEtappeDanachVerworfen(false)
            onSelect(selectedId === stage.id ? '' : stage.id)
          }}
          onEtappeDanachVerworfen={(verworfen) => {
            setStartVerworfen(false)
            setEtappeDanachVerworfen(verworfen)
          }}
        />
      ))}

      {/*
        Der Weg zur ersten Etappe. Bis hierher gab es ihn nicht: "Etappe
        danach" sitzt in einer aufgeklappten Etappenkarte, und ohne Etappe gibt
        es keine Karte, die sich aufklappen liesse. Solange jede Reise mit
        sechzehn Etappen der Vorlage begann, ist das niemandem aufgefallen -
        eine leere Reise war dadurch schlicht nicht zu benutzen.

        Gefragt wird dabei nach dem Startpunkt, und der Knopf wartet darauf:
        die erste Etappe ist die einzige, die ihren Start nirgendwoher
        übernehmen kann. Hier stand er fest verdrahtet - siehe ErsteEtappe.
      */}
      {leer && (
        <>
          <p className="leer">Noch keine Etappe geplant.</p>
          {darfSchreiben && (
            <ErsteEtappe
              onAnlegen={(start) => {
                const id = addStage('', start)
                // Eine leere Kennung heisst: es ist keine Etappe entstanden.
                // Stillschweigend übergehen liesse den Knopf tot aussehen.
                setStartVerworfen(id === '')
                // Auch dieser Weg ist eine Etappenhandlung, und mit ihr ist
                // der verworfene Versuch von vorhin erledigt - wie beim
                // Aufklappen einer Etappenkarte. Sonst überlebt "Die Etappe
                // ist inzwischen verschwunden" das erfolgreiche Anlegen der
                // ersten Etappe: die letzte Etappe der Reise auf einem anderen
                // Gerät gelöscht, die Reise dadurch leer, der Hinweis stehen
                // geblieben.
                setEtappeDanachVerworfen(false)
                if (id !== '') onSelect(id)
              }}
            />
          )}
        </>
      )}

      <p className="abschluss">
        {!darfSchreiben
          ? 'In dieser Reise hast du die Rolle Nur lesen: die Etappen stehen da, ändern lässt sich nichts.'
          : leer
            ? 'Eine Etappe ist ein Tag: Start, Ziel, Kilometer und Fahrzeit. Der Rest — Übernachtungen, Hinweise, Tulpen — kommt in der aufgeklappten Etappe dazu.'
            : 'Ändert sich die Route unterwegs: Etappe aufklappen, Werte überschreiben, verschieben oder auslassen. Karte, Summen und Budget ziehen automatisch nach.'}
      </p>
    </section>
  )
}

function StageCard({
  stage,
  tag,
  open,
  onToggle,
  onEtappeDanachVerworfen,
}: {
  stage: Stage
  /** Der Reisetag dieser Etappe, zweistellig - oder ein Strich. */
  tag: string
  open: boolean
  onToggle: () => void
  onEtappeDanachVerworfen: (verworfen: boolean) => void
}) {
  const { diary, darfSchreiben } = useRoadbook()
  const gefahren = diary[stage.id]?.done ?? false

  const klassen = ['etappe']
  if (open) klassen.push('offen')
  if (stage.optional) klassen.push('puffer')
  if (stage.skip) klassen.push('ausgelassen')

  return (
    <article className={klassen.join(' ')}>
      <button
        type="button"
        className="etappe-zeile"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={`etappe-${stage.id}`}
      >
        <span className="tagnummer">{tag}</span>
        <span>
          <span className="etappe-ziel">
            {stage.from} <span className="pfeil">→</span> {stage.to}
            {gefahren && <span className="stempel">gefahren</span>}
          </span>
          <span className="etappe-meta">
            <span className="land">{stage.land}</span>
            <span className={`marker typ-${stage.type}`}>{stage.type}</span>
            {stage.optional && <span className="marker puffer">Puffer</span>}
            {stage.skip && <span className="marker">ausgelassen</span>}
            {/* Eine veraltete Strecke fällt sonst erst auf, wenn man die
                Etappe aufklappt. Auf dem Papier hat der Hinweis nichts zu
                suchen: dort steht ohnehin die Luftlinie. */}
            {routeState(stage) === 'stale' && (
              <span className="marker veraltet kein-druck">Strecke veraltet</span>
            )}
          </span>
        </span>
        <span className="etappe-km">
          <b>{num(stage.km)}</b>
          <span>km</span>
        </span>
        <span className="etappe-h">
          <b>{hours(stage.h)}</b>
          <span>h</span>
        </span>
      </button>

      {open && (
        <div className="etappe-inhalt" id={`etappe-${stage.id}`}>
          <div className="spalten">
            <div>
              {stage.highlights.length > 0 && (
                <div className="block">
                  <h4>
                    <Mountain size={13} /> Unterwegs
                  </h4>
                  <ul>
                    {stage.highlights.map((eintrag, i) => (
                      <li key={i}>{eintrag}</li>
                    ))}
                  </ul>
                </div>
              )}

              {stage.camping.length > 0 && (
                <div className="block">
                  <h4>
                    <Tent size={13} /> Schlafen
                  </h4>
                  <ul>
                    {stage.camping.map((platz, i) => (
                      <li key={i}>
                        <b>{platz.name}</b> <span className="preis">{platz.price}</span>
                        {platz.note && (
                          <>
                            <br />
                            <small>{platz.note}</small>
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {stage.tulips && stage.tulips.length > 0 && (
                <div className="block">
                  <h4>
                    <Signpost size={13} /> Roadbook
                  </h4>
                  {/*
                    Dieselben Zeilen wie im Buch und in derselben Grösse: die
                    Ansicht am Bildschirm ist die Probe darauf, ob die Skizze
                    im Auto etwas sagt.
                  */}
                  <ul className="tulpenliste">
                    {stage.tulips.map((tulpe, i) => (
                      <li key={i}>
                        <span className="tulpen-km">{tulipKm(tulpe.km)}</span>
                        <TulipSketch tulip={tulpe} />
                        <span>
                          <b>{tulipText(tulpe)}</b>
                          {tulpe.note && <small>{tulpe.note}</small>}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {stage.night.length > 0 && (
                <div className="block">
                  <h4>
                    <Music size={13} /> Abends
                  </h4>
                  <ul>
                    {stage.night.map((eintrag, i) => (
                      <li key={i}>{eintrag}</li>
                    ))}
                  </ul>
                </div>
              )}

              {stage.tip && (
                <p className="hinweis">
                  <AlertTriangle size={13} /> {stage.tip}
                </p>
              )}
            </div>

            {darfSchreiben && (
              <StageEditor stage={stage} onEtappeDanachVerworfen={onEtappeDanachVerworfen} />
            )}
          </div>
        </div>
      )}
    </article>
  )
}

/**
 * Woher der Start dieser Etappe kommt - statt eines Felds, das ihn ändern
 * könnte.
 *
 * Anzeigen statt eingeben zu lassen ist hier die Entscheidung: der Start jeder
 * Etappe ausser der ersten ist das Ziel der vorherigen, und das ist die Kette,
 * die die Reise zusammenhält. Ein zweites Feld für dieselbe Stelle liesse sie
 * zerreissen, ohne dass es jemand merkt - im Buch stünde dann eine Etappe, die
 * dort anfängt, wo die vorherige gar nicht endet.
 *
 * Auseinanderlaufen kann sie trotzdem, nämlich beim Verschieben und Löschen:
 * wer eine Etappe nach vorn holt, hängt die andere hinter ein anderes Ziel.
 * Dann steht es hier, und daneben der Knopf, der es angleicht. Von selbst
 * geschieht das nicht - in der Startbestückung gibt es Ausflüge, die zum
 * Standort zurückführen (Bled - Bohinjsee - weiter ab Bled), und ein stilles
 * Zurechtrücken machte daraus eine Fahrt, die niemand geplant hat.
 */
function StartAusKette({
  stage,
  vorher,
  passt,
  onAngleichen,
}: {
  stage: Stage
  vorher: Stage
  passt: boolean
  onAngleichen: () => void
}) {
  /*
   * Hier steht bewusst keine Tagesnummer, sondern die Etappe davor.
   *
   * Gemeint ist die Nachbarschaft in der Liste, und die zählt die
   * ausgelassenen Etappen mit (siehe startHerkunft): eine Zahl, die es tut,
   * wäre kein Reisetag, und eine, die es nicht tut, zeigte auf die falsche
   * Zeile. Der Nachbar steht ohnehin direkt darüber - dorthin zeigt man mit
   * dem Finger und nicht mit einer Nummer.
   */
  if (passt) {
    return (
      <p className="leer">
        Start: {vorher.to || coordText(stage.c1)} · {coordText(stage.c1)} — das Ziel der Etappe
        davor.
      </p>
    )
  }

  return (
    <>
      <p className="karte-warnung">
        <AlertTriangle size={13} /> Diese Etappe beginnt bei {coordText(stage.c1)}, die Etappe davor
        endet aber bei {coordText(vorher.c2)} ({vorher.to}). Das kommt vom Verschieben oder Löschen
        einer Etappe — oder es ist so gewollt, etwa nach einem Ausflug, der zum Standort
        zurückführt.
      </p>
      <div className="knopfreihe">
        <button type="button" className="knopf still" onClick={onAngleichen}>
          Start auf das Ziel der Etappe davor setzen
        </button>
      </div>
    </>
  )
}

function StageEditor({
  stage,
  onEtappeDanachVerworfen,
}: {
  stage: Stage
  onEtappeDanachVerworfen: (verworfen: boolean) => void
}) {
  const { trip, setStage, addStage, deleteStage, shiftStage } = useRoadbook()
  const [loeschenBestaetigen, setLoeschenBestaetigen] = useState(false)

  // Wem der Start dieser Etappe gehört, entscheidet ihre Stelle in der Reise
  // und nicht, wann sie angelegt wurde: wird die erste gelöscht oder eine
  // andere nach vorn geschoben, wechselt er mit. Siehe startHerkunft.
  const herkunft = startHerkunft(trip.stages, stage.id)
  const vorher = herkunft.vorher

  return (
    <div className="kein-druck">
      <h4 className="block-titel">Etappe anpassen</h4>
      <div className="raster-2">
        <Field label="Von">
          <input
            value={stage.from}
            onChange={(e) => setStage(stage.id, { from: e.target.value })}
          />
        </Field>
        <Field label="Nach">
          <input value={stage.to} onChange={(e) => setStage(stage.id, { to: e.target.value })} />
        </Field>
        <Field label="Kilometer">
          <input
            type="number"
            inputMode="numeric"
            min={0}
            value={stage.km}
            onChange={(e) => setStage(stage.id, { km: Number(e.target.value) })}
          />
        </Field>
        <Field label="Fahrzeit h">
          <input
            type="number"
            inputMode="decimal"
            min={0}
            step={0.5}
            value={stage.h}
            onChange={(e) => setStage(stage.id, { h: Number(e.target.value) })}
          />
        </Field>
        <Field label="Land">
          <input
            value={stage.land}
            onChange={(e) => setStage(stage.id, { land: e.target.value })}
          />
        </Field>
        <Field label="Art">
          <select
            value={stage.type}
            onChange={(e) => setStage(stage.id, { type: e.target.value as Stage['type'] })}
          >
            {STAGE_TYPES.map((typ) => (
              <option key={typ} value={typ}>
                {typ}
              </option>
            ))}
          </select>
        </Field>
        {/* Der Start steht nur bei der ersten Etappe zur Eingabe. Alle
            anderen übernehmen ihn vom Ziel der vorherigen; ein Feld dort
            hiesse, die Kette zerreissen zu können, ohne dass es jemand
            merkt. */}
        {!vorher && (
          <>
            <Field label="Start Breite">
              <input
                type="number"
                inputMode="decimal"
                step={0.001}
                value={stage.c1[0]}
                onChange={(e) => setStage(stage.id, { c1: [Number(e.target.value), stage.c1[1]] })}
              />
            </Field>
            <Field label="Start Länge">
              <input
                type="number"
                inputMode="decimal"
                step={0.001}
                value={stage.c1[1]}
                onChange={(e) => setStage(stage.id, { c1: [stage.c1[0], Number(e.target.value)] })}
              />
            </Field>
          </>
        )}
        <Field label="Ziel Breite">
          <input
            type="number"
            inputMode="decimal"
            step={0.001}
            value={stage.c2[0]}
            onChange={(e) => setStage(stage.id, { c2: [Number(e.target.value), stage.c2[1]] })}
          />
        </Field>
        <Field label="Ziel Länge">
          <input
            type="number"
            inputMode="decimal"
            step={0.001}
            value={stage.c2[1]}
            onChange={(e) => setStage(stage.id, { c2: [stage.c2[0], Number(e.target.value)] })}
          />
        </Field>
      </div>

      {vorher ? (
        <StartAusKette
          stage={stage}
          vorher={vorher}
          passt={herkunft.passt}
          onAngleichen={() => setStage(stage.id, { c1: [vorher.c2[0], vorher.c2[1]] })}
        />
      ) : (
        <>
          {/* Dieselbe Suche wie für das Ziel, und aus demselben Grund: Breite
              und Länge abzutippen ist mühsam und fehleranfällig. */}
          <AddressSearch
            beschriftung="Startadresse suchen"
            platzhalter={`Ort oder Adresse, z. B. ${stage.from || 'Dornacherstrasse 210, Basel'}`}
            vorgabe={stage.from}
            onPick={(at) => setStage(stage.id, { c1: at })}
          />
          <p className="leer">
            Die erste Etappe bestimmt ihren Start selbst — sie hat keine vorherige, von der sie ihn
            übernehmen könnte. Jede weitere beginnt am Ziel der Etappe davor.
          </p>
        </>
      )}

      {/* Die Suche steht unter den Zahlenfeldern und ersetzt sie nicht: für
          eine neue Reise ist das Abtippen von Breite und Länge mühsam und
          fehleranfällig, aber wer eine Koordinate aus einer anderen Quelle hat,
          trägt sie weiterhin ein - und im Funkloch gibt es keine Suche. */}
      <AddressSearch
        beschriftung="Zieladresse suchen"
        platzhalter={`Ort oder Adresse, z. B. ${stage.to || 'Bled, Slovenija'}`}
        vorgabe={stage.to}
        onPick={(at) => setStage(stage.id, { c2: at })}
      />

      {/* Die berechnete Strecke gehört direkt unter die Koordinaten: sie
          hängt an ihnen, und wer eine verschiebt, sieht hier gleich, dass die
          gespeicherte Strecke nicht mehr dorthin führt. */}
      <StageRouteEditor stage={stage} onChange={(patch) => setStage(stage.id, patch)} />

      {/* Und gleich darunter, was auf dieser Strecke liegt: der Belag hängt an
          genau der Linie, die eben berechnet wurde, und die Frage „fahre ich da
          durch oder aussen herum“ stellt sich beim Planen und nicht später. */}
      <StageSurfacePanel stage={stage} onChange={(patch) => setStage(stage.id, patch)} />

      {/* Inhalte der Etappe. Sie schreiben über dasselbe setStage wie die
          Felder oben, nur eben ganze Listen statt einzelner Werte. */}
      <StageContentEditor stage={stage} onChange={(patch) => setStage(stage.id, patch)} />

      <div className="knopfreihe">
        <button type="button" className="knopf still" onClick={() => shiftStage(stage.id, -1)}>
          <ChevronUp size={14} /> Früher
        </button>
        <button type="button" className="knopf still" onClick={() => shiftStage(stage.id, 1)}>
          <ChevronDown size={14} /> Später
        </button>
        <button
          type="button"
          className="knopf still"
          onClick={() => setStage(stage.id, { skip: !stage.skip })}
        >
          {stage.skip ? 'Wieder einplanen' : 'Auslassen'}
        </button>
        <button
          type="button"
          className="knopf"
          onClick={() => onEtappeDanachVerworfen(addStage(stage.id) === '')}
        >
          <Plus size={14} /> Etappe danach
        </button>
        {loeschenBestaetigen ? (
          <>
            <button type="button" className="knopf gefahr" onClick={() => deleteStage(stage.id)}>
              <Trash2 size={14} /> Wirklich löschen
            </button>
            <button
              type="button"
              className="knopf still"
              onClick={() => setLoeschenBestaetigen(false)}
            >
              Abbrechen
            </button>
          </>
        ) : (
          <button
            type="button"
            className="knopf gefahr"
            onClick={() => setLoeschenBestaetigen(true)}
          >
            <Trash2 size={14} /> Löschen
          </button>
        )}
      </div>
      <p className="abschluss">
        Tagebuch und Fotos einer gelöschten Etappe bleiben erhalten und tauchen wieder auf, wenn du
        eine Etappe mit derselben Kennung anlegst.
      </p>
    </div>
  )
}
